import {
  Col,
  Dropdown,
  Form,
  InputNumber,
  message,
  Modal,
  Row,
  SelectProps,
  Space,
  Switch,
  ColorPicker,
  Divider,
  Input,
  InputRef,
  Spin,
} from "antd";
import { DownOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { FC, useEffect, useRef, useState } from "react";
import { CategoryType } from "../../../dto/Category.dto";
import { CreateWatchFaceRequest } from "../../../dto/request/create-watchface-request.dto";
import { SearchFilterType } from "../../../dto/SearchFilter.dto";
import {
  ComplicationModularUltraContent,
  ComplicationModularUltraStyle,
  ComplicationNight,
  ComplicationSystemSlotType,
  supportedSlotMimeTypes,
  supportedVideoPreviewMimeTypes,
  supportedWfAnimateMimeTypes,
  supportedWfPhotoMimeTypes,
  WatchFace,
  WatchFaceType,
  WFBottomWidgetType,
  WFComplicationType,
  WFParmesanStyle,
  WFStatus,
  WFTimeColor,
  WFTimeColorHex,
  WFTimeColorLabel,
  WFTimePosition,
  WFTimePositionOptions,
  WFTimeStyle,
  WFTimeStyleOption,
  WFTopWidgetType,
} from "../../../dto/WatchFace.dto";
import { useGetCategories } from "../../../services/api/category";
import { useGetSearchFilters } from "../../../services/api/searchFilters";
import {
  RATE,
  useCreateWatchFace,
  useGenerateComplicationVideoPreview,
  useGetDescriptionsByCategory,
  useGetTagsByCategory,
  useUpdateWatchFace,
} from "../../../services/api/watchface";
import {
  getCornerSlots,
  getSlots,
  getSystemSlot,
  getSystemSlots,
} from "../../../utils/complication";
import Button from "../Controls/Button";
import Select from "../Controls/Select";
import TextAreaInput from "../Controls/TextArea";
import TextInput from "../Controls/TextInput";
import PositionInput from "../Controls/PositionInput";
import ComplicationFormFields from "./ComplicationFormFields";
import UploadPreview from "./UploadPreview";
import { DefaultOptionType } from "antd/es/select";
import { randIntInclusive } from "../../../utils/randIntInclusive";
import SortableTagsInput from "../Controls/SortableTagsInput";

const FAKE_INSTALLS_MIN = 3000;
const FAKE_INSTALLS_MAX = 7000;
const FAKE_LIKES_PERCENT_MIN = 5;
const FAKE_LIKES_PERCENT_MAX = 20;

const WEATHER_GIF_CODES = [
  "50n",
  "50d",
  "13n",
  "13d",
  "11n",
  "11d",
  "10n",
  "10d",
  "09n",
  "09d",
  "04n",
  "04d",
  "03n",
  "03d",
  "02d",
  "02n",
  "01n",
  "01d",
] as const;

const DEFAULT_WEATHER_GIF_CODE = "02d";
const DEFAULT_FORECAST_ICON_COUNT = 3;
const DEFAULT_FORECAST_LEADING_OFFSET = 10;
const DEFAULT_FORECAST_TOP_OFFSET = 10;

const weatherGifCodeOptions: SelectProps["options"] = WEATHER_GIF_CODES.map(
  (code) => ({
    label: code,
    value: code,
  }),
);

const createDefaultForecastIcons = () =>
  Array.from({ length: DEFAULT_FORECAST_ICON_COUNT }, () => ({
    weatherCode: DEFAULT_WEATHER_GIF_CODE,
    leadingOffset: DEFAULT_FORECAST_LEADING_OFFSET,
    topOffset: DEFAULT_FORECAST_TOP_OFFSET,
  }));

interface ForecastVideoPreviewIcon {
  weatherCode: string;
  leadingOffset: number;
  topOffset: number;
}

interface VideoPreviewGenerateFormValues {
  weatherGifCode: string;
  forecastIcons: ForecastVideoPreviewIcon[];
}

interface OnChangeHandler {
  (e: any): void;
}

interface CreateWatchFaceFormProps {
  onSubmit?: OnChangeHandler;
  onFinish?: OnChangeHandler;
  onChange?: OnChangeHandler;
  onCancel?: OnChangeHandler;
  onOk?: OnChangeHandler;
  isOpen?: boolean;
  watchface?: WatchFace;
  defaultCategoryId?: string;
}

export type FieldType = Partial<CreateWatchFaceRequest>;

const wfTypeOption: SelectProps["options"] = [
  { label: "Photo", value: WatchFaceType.Photo },
  { label: "Portrait", value: WatchFaceType.Portrait },
  { label: "Animated", value: WatchFaceType.Animated },
  { label: "Complication", value: WatchFaceType.Complication },
  { label: "Parmesan", value: WatchFaceType.Parmesan },
  { label: "Edge To Edge / Full Screen", value: WatchFaceType.EdgeToEdge },
];
const wfStatus: SelectProps["options"] = [
  { label: "Published", value: WFStatus.Published },
  { label: "Unpublished", value: WFStatus.Unpublished },
];

const wfParmesanStyles: SelectProps["options"] = [
  { label: "Small", value: WFParmesanStyle.Small },
  { label: "Medium", value: WFParmesanStyle.Medium },
  { label: "Large", value: WFParmesanStyle.Large },
  { label: "XLarge", value: WFParmesanStyle.XLarge },
];

const wfTimeColorDefaultOptions: SelectProps["options"] = Object.values(
  WFTimeColor
).map((value) => {
  return {
    label: WFTimeColorLabel[value as WFTimeColor],
    value: value,
  };
});

const CreateWatchFaceForm: FC<any> = ({
  onSubmit,
  onFinish,
  onChange,
  onCancel,
  onOk,
  watchface,
  isOpen,
  defaultCategoryId,
  ...args
}: CreateWatchFaceFormProps) => {
  const [form] = Form.useForm();
  const [videoPreviewForm] = Form.useForm<VideoPreviewGenerateFormValues>();
  const [isVideoPreviewModalOpen, setIsVideoPreviewModalOpen] = useState(false);
  const wfTimePosition = Form.useWatch("wfTimePosition", form);
  const categoryId = Form.useWatch("categoryId", form);
  const wfType: WatchFaceType = Form.useWatch("wfType", form);
  const tags: string[] = Form.useWatch("tags", form);
  const wfTimeColor: {
    builderValue: string;
    hexValue: string;
  } = Form.useWatch("wfTimeColor", form);

  const complicationType: WFComplicationType = Form.useWatch(
    "complicationType",
    form
  );

  const [topWidgetOptions, setTopWidgetOptions] = useState<
    SelectProps["options"]
  >([]);
  const [bottomWidgetOptions, setBottomWidgetOptions] = useState<
    SelectProps["options"]
  >([]);

  const [wfTimePositionOption, setWfTimePositionOption] = useState<
    SelectProps["options"]
  >([]);
  const [wfTimeStyleOption, setWfTimeStyleOption] = useState<
    SelectProps["options"]
  >([]);
  const [wfTimeColorOptions, setWfTimeColorOptions] = useState<
    SelectProps["options"]
  >(wfTimeColorDefaultOptions);

  const [defaultPreview, setDefaultPreview] = useState<any>([]);
  const [defaultVideoPreview, setDefaultVideoPreview] = useState<any>([]);
  const [defaultDepth, setDefaultDepth] = useState<any>([]);
  const [defaultArchive, setDefaultArchive] = useState<any>([]);
  const [defaultPopulateCoefficient, setDefaultPopulateCoefficient] =
    useState<number>(1);
  const [slots, setSlots] = useState<any>({});

  useEffect(() => {
    return () => {
      form.resetFields();
      setDefaultDepth([]);
      setDefaultPreview([]);
      setDefaultVideoPreview([]);
      setDefaultArchive([]);
      setSlots({});
    };
  }, []);

  useEffect(() => {
    if (wfType === WatchFaceType.EdgeToEdge) {
      form.setFieldValue(
        "populateCoefficient",
        watchface?.populateCoefficient ?? 0.2
      );
    } else {
      form.setFieldValue(
        "populateCoefficient",
        watchface?.populateCoefficient ?? 1
      );
    }

    if ([WFComplicationType.ModularUltra].includes(complicationType)) {
      form.setFieldValue(
        "content",
        watchface?.complicationsInfo?.content ??
          ComplicationModularUltraContent.SECONDS
      );
      form.setFieldValue(
        "ultraStyle",
        watchface?.complicationsInfo?.ultraStyle ??
          ComplicationModularUltraStyle.DEFAULT_WITH_SECONDS
      );
      form.setFieldValue(
        "night",
        watchface?.complicationsInfo?.night ?? ComplicationNight.AUTO
      );
    }

    if (
      wfType &&
      wfType !== WatchFaceType.Complication &&
      wfType !== WatchFaceType.EdgeToEdge
    ) {
      setTopWidgetOptions(
        WFTopWidgetType[wfType].map((el) => {
          return { label: el, value: el };
        })
      );
      setBottomWidgetOptions(
        WFBottomWidgetType[wfType].map((el) => {
          return { label: el, value: el };
        })
      );
      setWfTimePositionOption(
        WFTimePositionOptions[wfType].map((el) => {
          return { label: el, value: el };
        })
      );
    }
    if (
      wfType &&
      [
        WatchFaceType.Animated,
        WatchFaceType.Portrait,
        WatchFaceType.Parmesan,
      ].includes(wfType)
    ) {
      setWfTimeStyleOption(
        WFTimeStyleOption[wfType].map((el) => {
          return { label: el, value: el };
        })
      );
    }

    if (wfType === WatchFaceType.Animated) {
      form.setFieldValue('wfTimeStyle', WFTimeStyle.Photo);
    }
    
    if (wfType && complicationType) {
      const availableSlots = getSlots(complicationType, {
        faceStyle: watchface?.complicationsInfo?.faceStyle,
      });

      const systemSlot = getSystemSlots(complicationType, {
        faceStyle: watchface?.complicationsInfo?.faceStyle,
      });

      const cornerSlot = getCornerSlots(complicationType, {
        faceStyle: watchface?.complicationsInfo?.faceStyle,
      });

      const temp = slots;

      availableSlots.forEach((type) => {
        if (watchface?.complicationsInfo?.[type]) {
          temp[type] = { ...watchface?.complicationsInfo?.[type] };
        } else {
          temp[type] = { ...temp[type] };
        }
      });

      cornerSlot.forEach((type) => {
        if (watchface?.complicationsInfo?.[type]) {
          temp[type] = { ...watchface?.complicationsInfo?.[type] };
        } else {
          temp[type] = { ...temp[type] };
        }
      });

      systemSlot.forEach((el) => {
        const type = getSystemSlot(el);
        if (type && watchface?.complicationsInfo?.[type]) {
          temp[type] = { ...watchface?.complicationsInfo?.[type] };
        } else {
          temp[type] = { ...temp[type] };
        }
      });

      setSlots(temp);
    }
  }, [wfType, complicationType]);

  useEffect(() => {
    form.setFieldValue("wfTimeColor", {
      ...wfTimeColor,
      hexValue:
        WFTimeColorHex[wfTimeColor?.builderValue as WFTimeColor] || "#FFFFFF",
    });
  }, [wfTimeColor?.builderValue]);

  const { data: categories } = useGetCategories({
    limit: 100,
    page: 1,
    type: watchface?.category?.type,
  });

  const { data: defaultTags } = useGetTagsByCategory({ category: categoryId });
  const { data: defaultDescriptions } = useGetDescriptionsByCategory({
    category: categoryId,
  });

  const { data: searchFilters } = useGetSearchFilters({
    categoryType: CategoryType.Admin,
    type: SearchFilterType.SearchTag,
  });

  const { mutate: createWatchFace, isLoading: isCreating } = useCreateWatchFace({
    onSuccess: (data) => {
      form.resetFields();
      console.log(data);
      onOk?.(data);
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.error?.message || "Something went wrong";
      const errorArray = err?.response?.data?.error?.errors;
      if (Array.isArray(errorArray)) {
        errorArray.forEach((element) => {
          const errors = element.constraints;
          message.error(Object.values(errors)[0]?.toString());
        });
      } else {
        message.error(errorMessage);
      }
    },
  });

  const { mutate: generateVideoPreview, isLoading: isGeneratingVideoPreview } =
    useGenerateComplicationVideoPreview({
      onSuccess: (data) => {
        message.success("Video preview generated");
        if (data?.complicationsInfo?.videoPreviewUrl) {
          setDefaultVideoPreview([
            {
              uid: "-1",
              name: data.complicationsInfo.videoPreviewId,
              status: "done",
              url: data.complicationsInfo.videoPreviewUrl,
            },
          ]);
          form.setFieldValue("videoPreview", undefined);
        }
      },
      onError: (err) => {
        const errorMessage =
          err?.response?.data?.error?.message || "Failed to generate video preview";
        message.error(errorMessage);
      },
    });

  const { mutate: updateWatchFace, isLoading: isUpdating } = useUpdateWatchFace({
    onSuccess: (data) => {
      form.resetFields();
      console.log(data);
      onOk?.(data);
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.error?.message || "Something went wrong";
      const errorArray = err?.response?.data?.error?.errors;
      if (Array.isArray(errorArray)) {
        errorArray.forEach((element) => {
          const errors = element.constraints;
          message.error(Object.values(errors)[0]?.toString());
        });
      } else {
        message.error(errorMessage);
      }
    },
  });

  const parseVersion = (version: string | number[] | undefined): number[] | undefined => {
    if (!version) return undefined;
    if (Array.isArray(version)) return version;
    if (typeof version === 'string') {
      const parts = version.split('.').map(Number).filter(n => !isNaN(n));
      return parts.length === 3 ? parts : undefined;
    }
    return undefined;
  };

  const handleOnFinish = async (values: any): Promise<void> => {
    const slotsInfo = {} as any;

    const slotsFile = [] as Array<any>;

    let tempIndex = 0;
    Object.entries(slots).forEach(([key, value], index) => {
      slotsInfo[key] = { ...slots[key], file: undefined };
      if (
        Object.values(ComplicationSystemSlotType).includes(
          key as ComplicationSystemSlotType
        )
      ) {
        return;
      }
      if (slots[key]?.file) {
        slotsFile.push(slots[key]?.file);
        slotsInfo[key].index = tempIndex;
        tempIndex = tempIndex + 1;
      }
      if (slots[key]?.file === null) {
        slotsInfo[key] = null;
      }
    });

    const minAppVersion = parseVersion(values.minAppVersion);
    const maxAppVersion = parseVersion(values.maxAppVersion);

    const submitData = { ...values, slots: slotsFile };
    if (!(values.videoPreview instanceof File)) {
      delete submitData.videoPreview;
    }
    if (values.videoPreview === null) {
      submitData.videoPreview = null;
    }

    if (watchface?.id) {
      updateWatchFace({
        id: watchface?.id,
        data: {
          ...submitData,
          position: values.position ? +values.position : undefined,
          fakeLikes: values.fakeLikes ? +values.fakeLikes : undefined,
          fakeInstalls: values.fakeInstalls ? +values.fakeInstalls : undefined,
          slotsInfo: JSON.stringify(slotsInfo),
          minAppVersion,
          maxAppVersion,
          wfTimeColor: values?.wfTimeColor?.hexValue
            ? {
                builderValue: values?.wfTimeColor?.builderValue,
                hexValue:
                  typeof values.wfTimeColor.hexValue === "string"
                    ? values.wfTimeColor.hexValue
                    : values.wfTimeColor.hexValue?.toHexString(),
              }
            : undefined,
        },
      });
    } else {
      createWatchFace({
        ...submitData,
        position: values.position ? +values.position : undefined,
        fakeLikes: values.fakeLikes ? +values.fakeLikes : undefined,
        fakeInstalls: values.fakeInstalls ? +values.fakeInstalls : undefined,
        slotsInfo: JSON.stringify(slotsInfo),
        minAppVersion,
        maxAppVersion,
        wfTimeColor: values?.wfTimeColor?.hexValue
          ? {
              builderValue: values?.wfTimeColor?.builderValue,
              hexValue:
                typeof values.wfTimeColor.hexValue === "string"
                  ? values.wfTimeColor.hexValue
                  : values.wfTimeColor.hexValue?.toHexString(),
            }
          : undefined,
      });
    }
  };

  const handleOnChange = (e: any): void => {
    onChange?.(e);
  };

  const handleOnCancel = (e: any): void => {
    form.resetFields();
    onCancel?.(e);
  };

  useEffect(() => {
    if (tags && tags.length) {
      return;
    }
    if (form.isFieldTouched("tags")) {
      return;
    }
    if (!defaultTags || !defaultTags.length) {
      return;
    }

    form.setFieldValue("tags", defaultTags);
  }, [form, defaultTags, tags]);

  useEffect(() => {
    if (watchface === null && defaultCategoryId) {
      form.setFieldValue('categoryId', defaultCategoryId);
    }
    
    if (watchface === null) {
      form.setFieldValue('status', WFStatus.Published);
    }

    if (watchface === null) {
      return;
    }

    const relativeOffset = watchface?.styles?.relativeOffset
      ? (watchface.styles.relativeOffset * RATE).toFixed(16)
      : undefined;
    const wfTimeRelativeSize = watchface?.styles?.wfTimeRelativeSize
      ? (watchface.styles.wfTimeRelativeSize * RATE).toFixed(16)
      : undefined;

    const searchFilters = watchface?.searchFilters?.map(
      (searchFilter) => searchFilter.id
    );

    const minAppVersion = watchface?.minAppVersion
      ? watchface.minAppVersion.join('.')
      : undefined;
    const maxAppVersion = watchface?.maxAppVersion
      ? watchface.maxAppVersion.join('.')
      : undefined;

    if (watchface) {
      const { position, ...watchfaceWithoutPosition } = watchface;
      form.setFieldsValue({
        ...watchfaceWithoutPosition,
        ...watchface?.styles,
        ...watchface?.complicationsInfo,
        wfType: watchface?.wfType,
        complicationType: watchface?.complicationsInfo?.wfType,
        relativeOffset,
        wfTimeRelativeSize,
        searchFilters,
        minAppVersion,
        maxAppVersion,
      });
    }

    (watchface?.styles?.previewUrl ||
      watchface?.complicationsInfo?.previewUrl) &&
      setDefaultPreview([
        {
          uid: "-1",
          name:
            watchface?.styles?.previewId ||
            watchface?.complicationsInfo?.previewId,
          status: "done",
          url:
            watchface?.styles?.previewUrl ||
            watchface?.complicationsInfo?.previewUrl,
        },
      ]);
    watchface?.complicationsInfo?.videoPreviewUrl &&
      setDefaultVideoPreview([
        {
          uid: "-1",
          name: watchface?.complicationsInfo?.videoPreviewId,
          status: "done",
          url: watchface?.complicationsInfo?.videoPreviewUrl,
        },
      ]);
    watchface?.styles?.depthUrl &&
      setDefaultDepth([
        {
          uid: "-1",
          name: watchface?.styles?.depthId,
          status: "done",
          url: watchface?.styles?.depthUrl,
        },
      ]);

    watchface?.edgeToEdgeInfo?.previewId &&
      setDefaultArchive([
        {
          uid: "-1",
          name: watchface?.edgeToEdgeInfo?.previewId,
          status: "done",
          url: watchface?.edgeToEdgeInfo?.archiveUrl,
        },
      ]);

    watchface?.edgeToEdgeInfo?.archiveId &&
      setDefaultPreview([
        {
          uid: "-1",
          name: watchface?.edgeToEdgeInfo?.archiveId,
          status: "done",
          url: watchface?.edgeToEdgeInfo?.previewUrl,
        },
      ]);
    return () => {};
  }, [form, watchface]);


  // Добавляем случайные лайки и установки
  useEffect(() => {
    if (!categoryId) return;
    const category = categories?.data?.find((el) => el.id === categoryId);
    if (!category) return;
    if (category.type === CategoryType.Customer) return;
    const fakeInstalls = form.getFieldValue("fakeInstalls");
    const fakeLikes = form.getFieldValue("fakeLikes");

    if (fakeInstalls >= 0 || fakeLikes >= 0) return;
    
    const newFakeInstalls = randIntInclusive(FAKE_INSTALLS_MIN, FAKE_INSTALLS_MAX);
    const newFakeLikes = Math.floor((newFakeInstalls * randIntInclusive(FAKE_LIKES_PERCENT_MIN, FAKE_LIKES_PERCENT_MAX)) / 100);
    form.setFieldValue("fakeInstalls", newFakeInstalls);
    form.setFieldValue("fakeLikes", newFakeLikes);
  }, [categoryId, form, categories?.data]);

  const [name, setName] = useState("");
  const inputRef = useRef<InputRef>(null);
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };
  const addItem = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (name.length > 0) {
      setWfTimeColorOptions([
        ...(wfTimeColorOptions as DefaultOptionType[]),
        { label: name, value: name },
      ]);
    }
    setName("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const isLoading = isCreating || isUpdating || isGeneratingVideoPreview;

  const handleOpenVideoPreviewModal = (): void => {
    if (!watchface?.id) {
      return;
    }
    videoPreviewForm.setFieldsValue({
      weatherGifCode: DEFAULT_WEATHER_GIF_CODE,
      forecastIcons: createDefaultForecastIcons(),
    });
    setIsVideoPreviewModalOpen(true);
  };

  const handleCloseVideoPreviewModal = (): void => {
    setIsVideoPreviewModalOpen(false);
  };

  const handleGenerateVideoPreview = async (): Promise<void> => {
    if (!watchface?.id) {
      return;
    }
    try {
      const values = await videoPreviewForm.validateFields();
      generateVideoPreview({
        watchfaceId: watchface.id,
        weatherGifCode: values.weatherGifCode,
        forecastIcons: values.forecastIcons,
      });
      setIsVideoPreviewModalOpen(false);
    } catch {
      // form validation errors are shown inline
    }
  };

  return (
    <Spin spinning={isLoading} tip="Saving...">
      <Form
        onChange={handleOnChange}
        onFinish={handleOnFinish}
        form={form}
        autoComplete="off"
        layout="vertical"
        {...args}
      >
      <Row>
        <Col span={24}>
          <Form.Item<FieldType>
            name={"categoryId"}
            label="Category"
            rules={[
              { required: true, message: "Please select watchface category!" },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="WatchFace Category"
              options={categories?.data?.map((el) => {
                return { label: `${el.name} - ${el.type}`, value: el.id };
              })}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            <Col span={6}>
              <Form.Item<FieldType>
                name={"isFree"}
                label="Is Free"
                valuePropName="checked"
              >
                <Switch defaultChecked={false} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item<FieldType>
                name={"isApproved"}
                label="Is Approved"
                valuePropName="checked"
              >
                <Switch defaultChecked={false} />
              </Form.Item>
            </Col>
            {categoryId && (() => {
              const category = categories?.data?.find((el) => el.id === categoryId);
              const isCustomerCategory = category?.type === CategoryType.Customer;
              return !isCustomerCategory ? (
                <Col span={12}>
                  <Form.Item<FieldType> name={"position"} label="Position">
                    <PositionInput
                      endpoint="/admin/watchface"
                      queryParamName="categoryId"
                      queryParamValue={categoryId}
                      entityId={watchface?.id}
                    />
                  </Form.Item>
                </Col>
              ) : null;
            })()}
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            <Col span={8}>
              <Form.Item<FieldType> name={"fakeLikes"} label="Fake Likes">
                <TextInput placeholder="Watchface fake Likes" type="number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item<FieldType> name={"fakeInstalls"} label="Fake Installs">
                <TextInput
                  placeholder="Watchface fake installs"
                  type="number"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item<FieldType>
                name={"populateCoefficient"}
                label="Coefficient"
              >
                <TextInput
                  placeholder="1"
                  type="number"
                  defaultValue={defaultPopulateCoefficient}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            <Col span={12}>
              <Form.Item<FieldType>
                name={"minAppVersion"}
                label="Min App Version"
              >
                <TextInput
                  placeholder="1.0.0"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<FieldType>
                name={"maxAppVersion"}
                label="Max App Version"
              >
                <TextInput
                  placeholder="99.99.99"
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            <Col span={12}>
              <Form.Item<FieldType>
                name={"status"}
                label="Watchface Status"
                rules={[
                  {
                    required: true,
                    message: "Please select watchface status!",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  placeholder="WatchFace Status"
                  options={wfStatus}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<FieldType>
                name={"wfType"}
                label="Watchface Type"
                rules={[
                  { required: true, message: "Please select watchface type!" },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  placeholder="WatchFace Type"
                  options={wfTypeOption}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={6}>
          <Form.Item<FieldType>
            name="preview"
            label="Preview"
            rules={[
              {
                required: !defaultPreview?.length,
                message: "Please upload watchface preview!",
              },
            ]}
          >
            <UploadPreview
              name="preview"
              supportType={
                wfType === WatchFaceType.Animated
                  ? supportedWfAnimateMimeTypes
                  : supportedWfPhotoMimeTypes
              }
              defaultFileList={defaultPreview}
            />
          </Form.Item>
        </Col>
        {[WatchFaceType.Complication].includes(wfType) && (
          <Col span={6}>
            <Form.Item<FieldType>
              name="videoPreview"
              label="Video"
              tooltip="Optional MP4. On create, auto-generated from GIF slots if empty. On update, use Generate video preview."
            >
              <UploadPreview
                name="videoPreview"
                supportType={supportedVideoPreviewMimeTypes}
                defaultFileList={defaultVideoPreview}
                maxSizeMb={20}
                isVideo
              />
            </Form.Item>
          </Col>
        )}
        {[WatchFaceType.Portrait, WatchFaceType.Parmesan].includes(wfType) && (
          <Col span={6}>
            <Form.Item<FieldType> name="depth" label="Depth">
              <UploadPreview
                name="depth"
                supportType={supportedWfPhotoMimeTypes}
                defaultFileList={defaultDepth}
              />
            </Form.Item>
          </Col>
        )}
        {[WatchFaceType.EdgeToEdge].includes(wfType) && (
          <Col span={6}>
            <Form.Item<FieldType> name="archive" label="Archive">
              <UploadPreview
                name="archive"
                supportType={supportedSlotMimeTypes}
                defaultFileList={defaultArchive}
              />
            </Form.Item>
          </Col>
        )}

        <Col span={24}>
          <Form.Item<FieldType>
            name={"name"}
            label="Name"
            rules={[
              { required: true, message: "Please input watchface name!" },
            ]}
          >
            <TextInput placeholder="WatchFace Name" />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item<FieldType>
            name={"description"}
            label="Description"
            rules={[
              {
                required: true,
                message: "Please input watchface description!",
              },
            ]}
          >
            <TextAreaInput
              autoSize={{ minRows: 2, maxRows: 6 }}
              maxLength={512}
              placeholder="WatchFace Description"
            />
          </Form.Item>
        </Col>

        {Boolean(defaultDescriptions?.length) && (
          <Col span={24} style={{ marginBottom: "24px" }}>
            <Dropdown
              menu={{
                items: defaultDescriptions?.map((el) => ({
                  label: el,
                  key: el,
                })),
                onClick: ({ key }) => form.setFieldValue("description", key),
              }}
            >
              <a onClick={(e) => e.preventDefault()}>
                <Space>
                  Description Default Values
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </Col>
        )}

        {[WatchFaceType.Parmesan].includes(wfType) && (
          <Col span={24}>
            <Row gutter={[20, 20]}>
              {[WatchFaceType.Parmesan].includes(wfType) && (
                <Col span={24}>
                  <Form.Item<FieldType>
                    name={"wfParmesanStyle"}
                    label="Parmesan Style"
                    rules={[
                      {
                        required: true,
                        message: "Please input watchface parmesan style!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                      placeholder="WatchFace Parmesan Style"
                      options={wfParmesanStyles}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Col>
        )}

        <Col span={24}>
          <Form.Item<FieldType>
            name={"tags"}
            label="Tags"
            rules={[
              {
                validator: (_, value) => {
                  if (Array.isArray(value) && value.length > 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Please input at least one tag!")
                  );
                },
              },
            ]}
          >
            <SortableTagsInput
              showSearch
              optionFilterProp="label"
              allowClear
              style={{ width: "100%" }}
              placeholder="Tags"
              options={defaultTags?.map((el) => {
                return { label: el, value: el };
              })}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item<FieldType> name={"searchFilters"} label="Search Filters">
            <Select
              showSearch
              optionFilterProp="label"
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Search Filters"
              options={searchFilters?.map((el) => ({
                label: el.name,
                value: el.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Row gutter={[20, 20]}>
            {[
              WatchFaceType.Portrait,
              WatchFaceType.Photo,
              WatchFaceType.Animated,
              WatchFaceType.Parmesan,
            ].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={"wfTimePosition"}
                  label="Time Position"
                  rules={[
                    {
                      required: true,
                      message: "Please select watchface time position!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="WatchFace Time Position"
                    options={wfTimePositionOption}
                  />
                </Form.Item>
              </Col>
            )}

            {wfTimePosition === WFTimePosition.Custom && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={"relativeOffset"}
                  label="Time Offset"
                  rules={[
                    {
                      required: wfTimePosition === WFTimePosition.Custom,
                      message: "Please select watchface time position!",
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={RATE}
                    style={{ width: "100%" }}
                    placeholder="WatchFace Relative Offset"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            {[
              WatchFaceType.Portrait,
              WatchFaceType.Animated,
              WatchFaceType.Parmesan,
            ].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={"topWFWidgetType"}
                  label="Top Widget"
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="WatchFace Top Widget"
                    options={topWidgetOptions}
                  />
                </Form.Item>
              </Col>
            )}

            {[
              WatchFaceType.Portrait,
              WatchFaceType.Animated,
              WatchFaceType.Parmesan,
            ].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={"bottomWFWidgetType"}
                  label="Bottom Widget"
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="WatchFace Bottom Widget"
                    options={bottomWidgetOptions}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            {[WatchFaceType.Parmesan].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={["wfTimeColor", "builderValue"]}
                  label="Time Color"
                  rules={[
                    {
                      required: true,
                      message: "Please select watchface time color!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="WatchFace Time Color"
                    options={wfTimeColorOptions}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: "8px 0" }} />
                        <Space style={{ padding: "0 8px 4px" }}>
                          <Input
                            placeholder="Please enter item"
                            ref={inputRef}
                            value={name}
                            onChange={onNameChange}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={addItem}
                          >
                            Add item
                          </Button>
                        </Space>
                      </>
                    )}
                  />
                </Form.Item>
              </Col>
            )}

            {[WatchFaceType.Parmesan].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={["wfTimeColor", "hexValue"]}
                  label="Time Color Hex Value"
                  rules={[
                    {
                      required:
                        !WFTimeColorHex[
                          wfTimeColor?.builderValue as WFTimeColor
                        ],
                      message: "Please select watchface time color hex value!",
                    },
                  ]}
                >
                  <ColorPicker
                    style={{ width: "100%" }}
                    showText
                    disabled={Boolean(
                      WFTimeColorHex[wfTimeColor?.builderValue as WFTimeColor]
                    )}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 20]}>
            {[
              WatchFaceType.Portrait,
              WatchFaceType.Animated,
              WatchFaceType.Parmesan,
            ].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType> name={"wfTimeStyle"} label="Time Style">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="WatchFace Time Style"
                    options={wfTimeStyleOption}
                  />
                </Form.Item>
              </Col>
            )}

            {[WatchFaceType.Portrait].includes(wfType) && (
              <Col span={12}>
                <Form.Item<FieldType>
                  name={"wfTimeRelativeSize"}
                  label="Time Relative Size"
                >
                  <InputNumber
                    min={1}
                    max={RATE}
                    style={{ width: "100%" }}
                    placeholder="WatchFace Time Relative Size"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>

        {[WatchFaceType.Complication].includes(wfType) && (
          <Col span={24}>
            <ComplicationFormFields
              form={form}
              setSlots={setSlots}
              slots={slots}
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col span={24} style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <Button htmlType="submit" loading={isLoading}>
            {watchface?.id ? "Update" : "Create"}
          </Button>
          {watchface?.id && wfType === WatchFaceType.Complication && (
            <Button
              type="default"
              onClick={handleOpenVideoPreviewModal}
              loading={isGeneratingVideoPreview}
              disabled={isLoading}
            >
              Generate video preview
            </Button>
          )}
          <Button onClick={handleOnCancel} disabled={isLoading}>
            Cancel
          </Button>
        </Col>
      </Row>
    </Form>
    <Modal
      title="Generate video preview"
      open={isVideoPreviewModalOpen}
      onOk={handleGenerateVideoPreview}
      onCancel={handleCloseVideoPreviewModal}
      okText="Generate"
      confirmLoading={isGeneratingVideoPreview}
      destroyOnClose
      width={720}
    >
      <Form
        form={videoPreviewForm}
        layout="vertical"
        initialValues={{
          weatherGifCode: DEFAULT_WEATHER_GIF_CODE,
          forecastIcons: createDefaultForecastIcons(),
        }}
      >
        <Form.Item
          name="weatherGifCode"
          label="Weather GIF icon"
          rules={[{ required: true, message: "Select weather icon" }]}
          tooltip="Icon code used for gifWeather animation in the preview"
        >
          <Select
            options={weatherGifCodeOptions}
            placeholder="Select weather icon"
          />
        </Form.Item>
        <Form.List name="forecastIcons">
          {(fields, { add, remove }) => (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <span>Forecast icons</span>
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      weatherCode: DEFAULT_WEATHER_GIF_CODE,
                      leadingOffset: DEFAULT_FORECAST_LEADING_OFFSET,
                      topOffset: DEFAULT_FORECAST_TOP_OFFSET,
                    })
                  }
                  icon={<PlusOutlined />}
                >
                  Add icon
                </Button>
              </div>
              {fields.map((field, index) => (
                <Space
                  key={field.key}
                  align="start"
                  style={{ display: "flex", marginBottom: 8 }}
                  wrap
                >
                  <Form.Item
                    {...field}
                    name={[field.name, "weatherCode"]}
                    label={index === 0 ? "Icon" : undefined}
                    rules={[{ required: true, message: "Select icon" }]}
                    style={{ minWidth: 120 }}
                  >
                    <Select options={weatherGifCodeOptions} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "leadingOffset"]}
                    label={index === 0 ? "Left offset" : undefined}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, "topOffset"]}
                    label={index === 0 ? "Top offset" : undefined}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber />
                  </Form.Item>
                  <MinusCircleOutlined
                    onClick={() => remove(field.name)}
                    style={{ marginTop: index === 0 ? 30 : 8 }}
                  />
                </Space>
              ))}
              {fields.length === 0 && (
                <div style={{ color: "#888", marginBottom: 8 }}>
                  No forecast icons. Add if the watchface has a forecast animation.
                </div>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
    </Spin>
  );
};

export default CreateWatchFaceForm;
